import React from 'react';
import { SectionContainer } from './SectionContainer';
import { Heading, LazyLoadContainer, colors, Text, Button, Pill } from '@web-for-marketing/react-ui';
import { Grid, Stack } from '@mui/material';
import { RichTextContent } from '@components/RichTextContent';
import { OutlinedCheckmarkIcon } from '@components/IconComponents/OutlinedCheckmarkIcon';
import { BulletPointsHeaderSectionData } from '@models/section/bulletPointsHeaderSection';
import { PopoutButton } from '@components/PopoutCustomSection/PopoutButton';
import { getLinkTargetValue } from './helpers/SectionComponentHelper';
import { RiveContent } from './RiveContent';
import { Picture } from '@components/Picture';
import { usePageVariant } from '@contexts/PageVariantContext';
import { splitPaddingValues } from './helpers/spacingStyleHelpers';

interface BulletPointsHeaderProps {
    sectionData: BulletPointsHeaderSectionData;
    lazyLoad: boolean;
    headerSection: boolean;
    hasBackground: boolean;
}

const classes = {
    list: {
        listStyleType: 'none',
        padding: 0,
        margin: 0,
    },
} as const;

export function BulletPointsHeader({
    sectionData,
    lazyLoad,
    headerSection,
    hasBackground,
}: BulletPointsHeaderProps): React.ReactElement {
    const {
        title,
        bulletPoints,
        buttons,
        media,
        label,
        headingVariant,
        subtitle,
        variantGroupTitle,
        theme,
        spacing = '8rem 0',
    } = sectionData;
    const { paddingTop, paddingBottom } = splitPaddingValues(spacing);
    const { selectedPageVariant, changeSelectedPageVariant, pageVariants } = usePageVariant();
    const isLightMode = theme === 'light';

    return (
        <div
            data-testid='bulletPointsHeaderBackground'
            css={!hasBackground ? { backgroundColor: colors.background.dark } : undefined}
        >
            <SectionContainer topSpacing={`${paddingTop}rem`} bottomSpacing={`${paddingBottom}rem`}>
                <Stack alignItems='center' gap='24px'>
                    {pageVariants && pageVariants.length ? (
                        <Stack alignItems='center' gap='16px'>
                            {variantGroupTitle ? (
                                <Text
                                    variant='body2'
                                    weight='medium'
                                    color={isLightMode ? 'primary' : 'inversePrimary'}
                                >
                                    {variantGroupTitle}
                                </Text>
                            ) : null}
                            <Stack
                                direction='row'
                                rowGap='16px'
                                columnGap='12px'
                                flexWrap='wrap'
                                justifyContent='center'
                                alignItems='center'
                            >
                                {pageVariants.map((variant) => (
                                    <Pill
                                        key={variant}
                                        variant={isLightMode ? 'primaryFilled' : 'inverseOutline'}
                                        size='small'
                                        isSelected={variant === selectedPageVariant}
                                        onClick={() => {
                                            changeSelectedPageVariant(variant);
                                        }}
                                    >
                                        {variant}
                                    </Pill>
                                ))}
                            </Stack>
                        </Stack>
                    ) : null}
                    <Grid container alignItems='center' spacing='24px'>
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Stack spacing='16px'>
                                {label ? (
                                    <Text
                                        variant='h5'
                                        color={isLightMode ? 'activePrimary' : 'inversePrimary'}
                                        weight='medium'
                                    >
                                        {label}
                                    </Text>
                                ) : null}
                                <Heading
                                    variant={headingVariant}
                                    component={headerSection ? 'h1' : 'h2'}
                                    color={isLightMode ? 'primary' : 'inversePrimary'}
                                >
                                    {title}
                                </Heading>
                                {subtitle ? (
                                    <Text
                                        variant='h4'
                                        weight='medium'
                                        component={headerSection ? 'h2' : 'h3'}
                                        color={isLightMode ? 'primary' : 'inversePrimary'}
                                    >
                                        {subtitle}
                                    </Text>
                                ) : null}
                                {bulletPoints.length ? (
                                    <Stack component='ul' spacing='16px' css={classes.list}>
                                        {bulletPoints.map((bulletPoint, index) => (
                                            <Stack direction='row' component='li' key={index} spacing='8px'>
                                                <OutlinedCheckmarkIcon
                                                    color={
                                                        isLightMode
                                                            ? colors.text.activePrimary
                                                            : colors.text.inversePrimary
                                                    }
                                                />
                                                <RichTextContent
                                                    content={bulletPoint}
                                                    color={isLightMode ? 'secondary' : 'inversePrimary'}
                                                />
                                            </Stack>
                                        ))}
                                    </Stack>
                                ) : null}
                                {buttons.length ? (
                                    <Stack direction='row' gap={2} flexWrap='wrap'>
                                        {buttons.map((button, index) =>
                                            button.isPopout ? (
                                                <PopoutButton
                                                    key={`${button.buttonText}-${index}`}
                                                    text={button.buttonText}
                                                    variant={button.buttonType || 'primary'}
                                                    color={button.buttonColor || 'blue'}
                                                    customSectionKey={button.popoutCustomSection}
                                                    displayMode={button.popoutDisplayMode}
                                                    backgroundImagePath={button.popoutBackgroundImage}
                                                    params={button.params}
                                                    aria-label={button.ariaLabel}
                                                    isV2
                                                />
                                            ) : (
                                                <Button
                                                    key={`${button.buttonText}-${index}`}
                                                    component='a'
                                                    variant={button.buttonType || 'primary'}
                                                    color={button.buttonColor || 'blue'}
                                                    href={button.buttonLinkPath}
                                                    target={getLinkTargetValue(button.buttonTarget)}
                                                    aria-label={button.ariaLabel}
                                                    className={button.buttonClassName}
                                                >
                                                    {button.buttonText}
                                                </Button>
                                            )
                                        )}
                                    </Stack>
                                ) : null}
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 12, md: 7 }}>
                            {media.type === 'image' && (media.content.imagePath || media.content.mobilePath) ? (
                                <LazyLoadContainer lazyload={lazyLoad}>
                                    <Picture
                                        imagePath={media.content.imagePath}
                                        mobilePath={media.content.mobilePath}
                                        alt={media.content.imageAlt}
                                        mobileAlt={media.content.mobileAlt}
                                        lazyLoad={lazyLoad}
                                        fullWidth
                                        aspectRatio={media.content.imageAspectRatio}
                                        mobileAspectRatio={media.content.mobileImageAspectRatio}
                                        css={{ borderRadius: '10px' }}
                                    />
                                </LazyLoadContainer>
                            ) : null}
                            {media.type === 'rive' && media.content.src ? (
                                <RiveContent content={media.content} lazyLoad={lazyLoad} />
                            ) : null}
                        </Grid>
                    </Grid>
                </Stack>
            </SectionContainer>
        </div>
    );
}
